import { useState, useEffect } from "react";
import {
  Sidebar,
  Menu,
  MenuItem,
  sidebarClasses,
  SubMenu,
} from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";

import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import BackupRoundedIcon from "@mui/icons-material/BackupRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
// import { FaSackDollar } from "react-icons/fa6";
import { GiTakeMyMoney } from "react-icons/gi";
import { GiMoneyStack } from "react-icons/gi";
import { FaSackDollar } from "react-icons/fa6";
import {
  Build,
  Diversity1,
  Diversity3,
  Engineering,
  NotificationsActive,
  Person2,
  WhatsApp,
} from "@mui/icons-material";
import { hasPermission } from "../../services/pemissions";
import { useUser } from "../../provider/AuthProvider";

import { useTranslation } from "react-i18next";

const Item = ({ title, to, icon, selected, setSelected, onClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleClick = () => {
    setSelected(to);
    if (onClick) onClick();
  };
  const menu_colors = selected === to ? "#ffffff" : "#ffffff";
  return (
    <MenuItem
      active={selected === to}
      // style={{ menu_colors }}
      onClick={handleClick}
      icon={icon}
      component={<Link to={to} />}
    >
      {/* <Link to={to} /> */}
      <Typography>{title}</Typography>
    </MenuItem>
  );
};

const SidebarItem = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { user, logout } = useUser();

  const { t, i18n } = useTranslation();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";

  const [selected, setSelected] = useState(() => {
    const path = location.pathname.split("/")[1]; // e.g., '/user' → 'user'
    return path.charAt(0).toUpperCase() + path.slice(1) || "Dashboard";
  });
  const navigate = useNavigate();

  useEffect(() => {
    // const path = location.pathname.split("/")[1];
    // setSelected(path.charAt(0).toUpperCase() + path.slice(1) || "Dashboard");
    setSelected(location.pathname);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
  };

  return (
    <Sidebar
      style={{ direction: direction }}
      collapsed={isCollapsed}
      rootStyles={{
        borderRight: "none",
        boxShadow:
          "inset -3px 0 0 rgba(255,255,255,0.05), 2px 0 10px rgba(0,0,0,0.4)",

        [`.${sidebarClasses.container}`]: {
          backgroundColor: "#0d1b2a",
        },
      }}
    >
      <Menu
        iconShape="square"
        menuItemStyles={{
          button: ({ level, active, disabled }) => {
            // only apply styles on first level elements of the tree
            if (level === 0)
              return {
                color: active ? "#0d1b2a" : "#DEDEDE", // active: dark text, inactive: light
                backgroundColor: active ? "#DEDEDE" : "#0d1b2a",
                "&:hover": {
                  backgroundColor: active ? "#DEDEDE" : "#22374D", // subtle hover on inactive
                  color: active ? "#0d1b2a" : "#DEDEDE", // hover text color
                },
                transition: "all 0.2s ease",
                borderRadius: "8px",
                margin: "0px 8px",
                padding: "10px 16px",
              };
          },
        }}
      >
        <Box display="flex" justifyContent="end" alignItems="end">
          <IconButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{ color: colors.grey[600] }}
          >
            <MenuOutlinedIcon />
          </IconButton>
        </Box>
        <Box mb="25px">
          <Box display="flex" justifyContent="center" alignItems="center">
            <img
              alt="main-logo"
              width="70px"
              height="70px"
              src={`../../assets/icons/main-logo-blue.png`}
              style={{ cursor: "pointer" }}
            />
          </Box>
        </Box>
        <Box paddingLeft={isCollapsed ? undefined : "10%"}>
          <Item
            title={t("sidebar.dashboard")}
            to="/"
            icon={<DashboardRoundedIcon sx={{ fontSize: 26 }} />}
            selected={selected}
            setSelected={setSelected}
          />
          {hasPermission(user) && (
            <Item
              title={t("sidebar.cmtManagement")}
              to="/cmt"
              icon={<Diversity3 sx={{ fontSize: 26 }} />}
              selected={selected}
              setSelected={setSelected}
            />
          )}
          {hasPermission(user, "canAddTenantLandlord") && (
            <Item
              title={t("sidebar.tenantsLandlords")}
              to="/tenant"
              icon={<Person2 sx={{ fontSize: 26 }} />}
              selected={selected}
              setSelected={setSelected}
            />
          )}
          {hasPermission(user, "canAddProvider") && (
            <Item
              title={t("sidebar.serviceProvider")}
              to="/serviceprovider"
              icon={<Engineering sx={{ fontSize: 26 }} />}
              selected={selected}
              setSelected={setSelected}
            />
          )}
          {hasPermission(user, "canAddUnit") && (
            <Item
              title={t("sidebar.properties")}
              to="/properties"
              icon={<ApartmentRoundedIcon sx={{ fontSize: 26 }} />}
              selected={selected}
              setSelected={setSelected}
            />
          )}
          {hasPermission(user, "canReport") && (
            <Item
              title={t("sidebar.reports")}
              to="/report"
              icon={<InsertChartOutlinedIcon sx={{ fontSize: 26 }} />}
              selected={selected}
              setSelected={setSelected}
            />
          )}
          {hasPermission(user, "canFinancial") && (
            <Item
              title={t("sidebar.financials")}
              to="/financial"
              icon={<FaSackDollar size={26} />}
              selected={selected}
              setSelected={setSelected}
            />
          )}
          {hasPermission(user, "canSubscription") && (
            <Item
              title={t("sidebar.subscription")}
              to="/subscription"
              icon={<ReceiptLongRoundedIcon sx={{ fontSize: 26 }} />}
              selected={selected}
              setSelected={setSelected}
            />
          )}
          {hasPermission(user, "canAnnouncement") && (
            <Item
              title={t("sidebar.announcement")}
              to="/announcement"
              icon={<CampaignRoundedIcon sx={{ fontSize: 26 }} />}
              selected={selected}
              setSelected={setSelected}
            />
          )}
          <Item
            title={t("sidebar.notification")}
            to="/notification"
            icon={<NotificationsActive sx={{ fontSize: 26 }} />}
            selected={selected}
            setSelected={setSelected}
          />
          {/* {hasPermission(user, "canWhatsApp") && (
            <Item
              title={t("sidebar.whatsapp")}
              to="/whatsapp"
              icon={<WhatsApp sx={{ fontSize: 26 }} />}
              selected={selected}
              setSelected={setSelected}
            />
          )} */}

          <Item
            title={t("sidebar.profile")}
            to="/profile"
            icon={<SettingsSuggestRoundedIcon sx={{ fontSize: 26 }} />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title={t("sidebar.logout")}
            to={null}
            icon={<LogoutRoundedIcon sx={{ fontSize: 26 }} />}
            selected={selected}
            setSelected={setSelected}
            onClick={handleLogout}
          />
        </Box>
      </Menu>
    </Sidebar>
  );
};

export default SidebarItem;
